import { roundRect2 } from '../../renderers';
import type { BasicObject } from '../../uiObjects/basicObject';
import { getBasicButton } from '../../uiObjects/button';

import { GRID_ADD_MODE, GRID_MOVE_MODE } from './addMoveMode';
import type { GridAddMoveMode } from './addMoveMode';
import { paintGridIconButtonLabel } from './iconButtonLabel';

interface GridDragModeButtonOptions {
  $canvas: JQuery<HTMLElement>;
  width: number;
  height: number;
  getThemeColor: () => string;
  getMode: () => GridAddMoveMode;
  setMode: (mode: GridAddMoveMode) => void;
  isSelected: () => boolean;
  isCorrect: () => boolean;
  isDisabled?: () => boolean;
  consumeDisabledClick?: () => boolean;
  onGrabFocus: () => void;
}

export type GridDragModeButton = BasicObject;

const isDragModeButtonDisabled = (
  correct: boolean,
  isDisabled?: () => boolean
): boolean => correct || Boolean(isDisabled?.());

const getNextMode = (mode: GridAddMoveMode): GridAddMoveMode =>
  mode === GRID_MOVE_MODE ? GRID_ADD_MODE : GRID_MOVE_MODE;

const GRID_DRAG_MODE_BLUE = '#2f80ed';
const ENABLED_BUTTON = 'lightgray';
const DISABLED_BUTTON = '#eeeeee';
const DISABLED_ICON = '#b8b8b8';
const ENABLED_ICON = '#333333';
const ACTIVE_ICON = '#ffffff';
const DRAG_LABEL = 'Drag';
const GRAB_HAND_SOURCE_SIZE = 520;
const GRAB_HAND_ICON_PATH =
  'M133,384 L36,287 C5,258 6,219 36,204 C65,188 93,195 98,217 L133,252 M68,334 L68,383 C68,449 119,490 184,490 L332,490 C366,490 388,481 411,459 L470,401 C518,354 524,278 492,214 C484,197 474,182 461,169 L323,33 C300,11 267,11 246,31 C225,51 222,80 225,75 L267,117 M198,187 L140,129 C121,108 125,78 149,65 C176,51 199,61 225,75 M133,252 L75,193 C57,175 58,146 78,128 C103,106 134,107 140,129 L198,187 M68,334 L133,384';
let grabHandIconPath: Path2D | undefined;

const canUseVectorHandIcon = (): boolean => typeof Path2D !== 'undefined';

const getGrabHandIconPath = (): Path2D => {
  if (!grabHandIconPath) {
    grabHandIconPath = new Path2D(GRAB_HAND_ICON_PATH);
  }

  return grabHandIconPath;
};

const paintFallbackDragHandIcon = (
  ctx: CanvasRenderingContext2D,
  centerX: number,
  top: number,
  color: string,
  scale: number
): void => {
  ctx.save();
  ctx.strokeStyle = color;
  ctx.lineWidth = 1.7 * scale;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  ctx.beginPath();

  ctx.moveTo(centerX - 5 * scale, top + 8 * scale);
  ctx.lineTo(centerX - 5 * scale, top + 3 * scale);

  ctx.moveTo(centerX - 2 * scale, top + 8 * scale);
  ctx.lineTo(centerX - 2 * scale, top + scale);

  ctx.moveTo(centerX + scale, top + 8 * scale);
  ctx.lineTo(centerX + scale, top + 2 * scale);

  ctx.moveTo(centerX + 4 * scale, top + 9 * scale);
  ctx.lineTo(centerX + 4 * scale, top + 5 * scale);

  ctx.moveTo(centerX - 6 * scale, top + 8 * scale);
  ctx.lineTo(centerX - 9 * scale, top + 6 * scale);
  ctx.lineTo(centerX - 10 * scale, top + 7 * scale);
  ctx.lineTo(centerX - 7 * scale, top + 12 * scale);
  ctx.lineTo(centerX - 4 * scale, top + 14 * scale);
  ctx.lineTo(centerX + 5 * scale, top + 14 * scale);
  ctx.lineTo(centerX + 8 * scale, top + 11 * scale);
  ctx.lineTo(centerX + 8 * scale, top + 8 * scale);

  ctx.stroke();
  ctx.restore();
};

const paintDragHandIcon = (
  ctx: CanvasRenderingContext2D,
  centerX: number,
  top: number,
  color: string,
  scale = 1
): void => {
  if (!canUseVectorHandIcon()) {
    paintFallbackDragHandIcon(ctx, centerX, top, color, scale);

    return;
  }

  const iconWidth = 17 * scale;
  const iconHeight = 15 * scale;

  ctx.save();
  ctx.strokeStyle = color;
  ctx.lineWidth = 28;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.translate(centerX - iconWidth / 2, top);
  ctx.scale(
    iconWidth / GRAB_HAND_SOURCE_SIZE,
    iconHeight / GRAB_HAND_SOURCE_SIZE
  );
  ctx.stroke(getGrabHandIconPath());
  ctx.restore();
};

export function getGridDragModeButton({
  $canvas,
  width,
  height,
  getThemeColor,
  getMode,
  setMode,
  isSelected,
  isCorrect,
  isDisabled,
  consumeDisabledClick,
  onGrabFocus,
}: GridDragModeButtonOptions): GridDragModeButton {
  const button = getBasicButton($canvas) as GridDragModeButton;

  button.gmmName = 'grid drag mode button';
  button.setAllDim(width, height);

  button.mouseMoveResponse = function () {
    if (
      button.mouseIsOver &&
      isSelected() &&
      !isDragModeButtonDisabled(isCorrect(), isDisabled)
    ) {
      $canvas.css('cursor', 'pointer');

      return;
    }

    $canvas.css('cursor', 'default');
  };

  button.paintMe = function (ctx) {
    ctx.save();

    const correct = isCorrect();
    const selected = isSelected();
    const disabled = !selected || isDragModeButtonDisabled(correct, isDisabled);
    const dragModeActive = getMode() === GRID_MOVE_MODE;
    const iconColor = !selected
      ? 'lightgray'
      : disabled
      ? DISABLED_ICON
      : dragModeActive || button.animated
      ? ACTIVE_ICON
      : ENABLED_ICON;

    if (!correct && selected) {
      const buttonColor = disabled
        ? DISABLED_BUTTON
        : button.animated
        ? getThemeColor()
        : dragModeActive
        ? GRID_DRAG_MODE_BLUE
        : ENABLED_BUTTON;

      roundRect2(
        ctx,
        0,
        0,
        width,
        height,
        6,
        buttonColor,
        undefined,
        undefined,
        true
      );
    }

    const buttonScale = Math.min(width, height) / 30;

    paintDragHandIcon(ctx, width / 2, 2 * buttonScale, iconColor, buttonScale);

    paintGridIconButtonLabel(ctx, DRAG_LABEL, width, height, iconColor);

    ctx.restore();
  };

  button.mouseDownResponse = function () {
    const selected = isSelected();
    const disabled = isDragModeButtonDisabled(isCorrect(), isDisabled);

    button.animates = selected && !disabled;

    if (!selected) {
      return false;
    }

    if (disabled) {
      return consumeDisabledClick ? consumeDisabledClick() : false;
    }

    onGrabFocus();
    setMode(getNextMode(getMode()));

    return true;
  };

  return button;
}
